import React, { useEffect, useState } from "react";
import {
  Box, Grid, Typography, Button, TextField, InputAdornment, Paper, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  alpha, MenuItem, Select, FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, PAGE_BG, BORDER,
  TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import {
  useGetPlaylistsQuery,
  useDeletePlaylistMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { IPlaylist, MenuType } from "../../../Api/Interface/api.interface";
import { MENU_TYPE_LABELS } from "../Edit/menuScheduleShared";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNotifier } from "../../../Core/Notifier";
import Pagination from "../../../Components/Pagination";
import ActionButtons from "../../../Components/ui/ActionButtons";
import CreatePlaylistDialog from "../../../Components/Playlist/CreatePlaylistDialog";

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) === 1 ? "" : "s"} ago`;
};

const PlaylistList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debounced = useDebounce(searchQuery, 300);

  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [companyFilter, setCompanyFilter] = useState<number | "">("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const { data, isLoading, isError, refetch } = useGetPlaylistsQuery({
    searchText: currentSearchQuery,
  });
  const [deletePlaylist] = useDeletePlaylistMutation();

  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const [createDialog, setCreateDialog] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);
  const [toDelete, setToDelete] = useState<IPlaylist | null>(null);

  useEffect(() => {
    if (debounced.length >= 2 || debounced === "") setCurrentSearchQuery(debounced);
  }, [debounced]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deletePlaylist(toDelete.id).unwrap();
      if (res.status) {
        showMessage("Playlist deleted");
        setOpenDelete(false); setToDelete(null);
        refetch();
      }
    } catch {
      showErrorMessage("Failed to delete playlist");
    }
  };

  if (isLoading) return <PageLoader height="20vh" />;

  const playlists: IPlaylist[] = Array.isArray(data?.data)
    ? ((data?.data as unknown) as IPlaylist[])
    : [];

  const filtered = playlists.filter(p => {
    if (statusFilter === "active" && !p.is_active) return false;
    if (statusFilter === "inactive" && p.is_active) return false;
    if (companyFilter && p.company_id !== companyFilter) return false;
    return true;
  });

  const exportToCsv = () => {
    if (filtered.length === 0) return;
    const head = ["Name", "Version", "Items", "Devices", "Status", "Last Updated"].join(",");
    const body = filtered.map(p => [
      p.name, p.version, p.items_count ?? 0, p.devices_count ?? 0,
      p.is_active ? "Active" : "Inactive", p.updated_at ?? "",
    ].map(c => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `playlists-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Top Bar ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${BORDER}`, px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <PlaylistPlayIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Playlists
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              {playlists.length} playlist{playlists.length === 1 ? "" : "s"} in catalog
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search playlists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) },
                "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 1.5 },
              },
            }}
          />
          <Button
            variant="outlined" size="small"
            onClick={() => setShowFilters(v => !v)}
            startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: showFilters ? GOLD : BORDER,
              color: showFilters ? NAVY : TEXT_PRIMARY,
              bgcolor: showFilters ? alpha(GOLD, 0.08) : "transparent",
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}
          >
            Filters
          </Button>
          <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />
          <Button
            variant="outlined" size="small"
            onClick={exportToCsv}
            startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: BORDER, color: TEXT_PRIMARY,
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}
          >
            Export
          </Button>
          <Button
            variant="contained" color="secondary" disableElevation size="small"
            onClick={() => setCreateDialog(true)}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": {
                background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)`,
                boxShadow: `0 6px 20px ${alpha(GOLD, 0.55)}`,
                transform: "translateY(-1px)",
              },
            }}
          >
            New Playlist
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>
        {showFilters && (
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: `1px solid ${BORDER}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <FilterListIcon sx={{ fontSize: 18, color: GOLD }} />
              <Typography fontSize={13} fontWeight={700} color={NAVY}>Filters</Typography>
            </Box>
            {isSuperAdmin && (
              <Box sx={{ mb: 2, maxWidth: 320 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>
                  Company
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={companyFilter === "" ? "" : String(companyFilter)}
                    onChange={(e) =>
                      setCompanyFilter(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    displayEmpty
                    sx={{ fontSize: 13, borderRadius: 1.5 }}
                  >
                    <MenuItem value="">All companies</MenuItem>
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
              {[
                { v: "", label: "All" },
                { v: "active", label: "Active" },
                { v: "inactive", label: "Inactive" },
              ].map(opt => (
                <Button key={opt.v} size="small"
                  onClick={() => setStatusFilter(opt.v as any)}
                  sx={{
                    textTransform: "none", fontSize: 12, fontWeight: 600,
                    borderRadius: 1.5, px: 1.75, border: "1px solid",
                    borderColor: statusFilter === opt.v ? GOLD : BORDER,
                    bgcolor: statusFilter === opt.v ? alpha(GOLD, 0.08) : "transparent",
                    color: statusFilter === opt.v ? NAVY : TEXT_SECONDARY,
                    "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
                  }}>
                  {opt.label}
                </Button>
              ))}
            </Box>
          </Paper>
        )}

        <Paper elevation={0} sx={{
          borderRadius: "12px", border: `1px solid ${BORDER}`,
          overflow: "hidden", boxShadow: "0 4px 12px rgba(11,30,45,0.06)",
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {(
                    [
                      "Playlist Name",
                      "Menu Type",
                      ...(isSuperAdmin ? ["Company"] : []),
                      "Version",
                      "Media Count",
                      "Assigned Devices",
                      "Status",
                      "Last Updated",
                      "Actions",
                    ] as string[]
                  ).map((h, i, arr) => (
                    <TableCell key={h}
                      sx={{
                        borderBottom: `1px solid ${BORDER}`,
                        background: `linear-gradient(180deg, ${alpha(NAVY, 0.06)} 0%, #f8fafc 100%)`,
                        fontSize: 11, fontWeight: 700, color: NAVY,
                        textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5,
                        textAlign: i === arr.length - 1 ? "right" : "left",
                      }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 9 : 8} align="center" sx={{ py: 6, color: TEXT_SECONDARY }}>
                      {isError ? "Error fetching data" : "No playlists yet — click \"New Playlist\""}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
                    <TableRow key={row.id}
                      sx={{
                        transition: "background 0.15s ease",
                        "&:hover": { bgcolor: "#f8fafc" },
                      }}>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: 1.5,
                            bgcolor: alpha("#7c3aed", 0.12),
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <PlaylistPlayIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                          </Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY }}>
                            {row.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                        <Chip size="small"
                          label={MENU_TYPE_LABELS[(row.menu_type as MenuType) || "standard_playlist"]}
                          sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#EEF2F6", color: NAVY }} />
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          {row.company ? (
                            <Chip
                              size="small"
                              label={row.company.name}
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                bgcolor: alpha(NAVY, 0.06),
                                color: NAVY,
                                border: `1px solid ${alpha(NAVY, 0.12)}`,
                              }}
                            />
                          ) : (
                            <Typography component="span" sx={{ fontSize: 13, color: TEXT_SECONDARY }}>—</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                        <Chip size="small" label={`v${row.version}`}
                          sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: "#eef2ff", color: "#4338ca",
                            border: "1px solid #c7d2fe",
                          }} />
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>
                        {row.items_count ?? 0}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>
                        {row.devices_count ?? 0}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                        <Chip size="small"
                          label={row.is_active ? "Active" : "Inactive"}
                          sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: row.is_active ? "#ecfdf5" : "#f1f5f9",
                            color: row.is_active ? "#047857" : "#64748b",
                            border: `1px solid ${row.is_active ? "#a7f3d0" : "#cbd5e1"}`,
                          }} />
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_SECONDARY }}>
                        {formatRelative(row.updated_at)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, textAlign: "right" }}>
                        <ActionButtons
                          onEdit={() => navigate(`/playlist/edit/${row.id}`)}
                          onDelete={() => { setToDelete(row); setOpenDelete(true); }}
                          deleteRequiresRole={["super_admin", "company_admin"]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {filtered.length > rowsPerPage && (
            <Box sx={{ p: 2, borderTop: `1px solid ${BORDER}` }}>
              <Pagination
                count={filtered.length} page={page} rowsPerPage={rowsPerPage}
                onPageChange={(_e, p) => setPage(p)}
              />
            </Box>
          )}
        </Paper>
      </Box>

      <CreatePlaylistDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        onCreated={(created) => {
          setCreateDialog(false);
          if (created?.id) navigate(`/playlist/edit/${created.id}`);
        }}
      />

      <DeletePopup
        open={openDelete}
        handleCloseDelete={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Delete Playlist"
        content={toDelete ? `Delete playlist "${toDelete.name}"? Devices assigned to it will lose their playlist.` : ""}
      />
    </Box>
  );
};

export default PlaylistList;
